import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Loader2, Pencil, LogOut, PlusCircle, User, X, Check } from 'lucide-react';
import {
  getAuthorityPositions,
  updateAuthorityPosition,
  endAuthorityPosition,
  addHistoricalAuthorityPosition,
  searchMinistersForAuthority,
} from '@/data/territoryRepository';
import type { AuthorityPosition, AuthorityRole } from '@/models/authorityPosition';
import {
  ROLE_LABELS,
  DISTRICT_ROLES,
  REGION_ROLES,
} from '@/models/authorityPosition';
import type { Minister } from '@/models/minister';
import type { District } from '@/models/district';
import type { Region } from '@/models/region';
import { useAuth } from '@/context/authContext';
import { isAdmin } from '@/models/user';
import { getUserProfileByAuthId } from '@/Services/users.service';

interface TerritoryContext {
  district: District | null;
  region: Region | null;
  isDistrict: boolean;
  refreshTerritory: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Edit modal — dates + notes only (minister cannot change here)
// ---------------------------------------------------------------------------
interface EditModalProps {
  position: AuthorityPosition;
  onClose: () => void;
  onSaved: () => void;
}

function EditModal({ position, onClose, onSaved }: EditModalProps) {
  const [startedAt, setStartedAt] = useState(position.started_at);
  const [endedAt, setEndedAt] = useState(position.ended_at ?? '');
  const [notes, setNotes] = useState(position.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isActive = !position.ended_at;

  const handleSave = async () => {
    if (!startedAt) { setError('La fecha de inicio es obligatoria.'); return; }
    if (!isActive && !endedAt) { setError('La fecha de fin es obligatoria para registros históricos.'); return; }
    if (endedAt && endedAt < startedAt) { setError('La fecha de fin no puede ser anterior a la de inicio.'); return; }

    setSaving(true);
    setError('');
    try {
      await updateAuthorityPosition(position.id, {
        started_at: startedAt,
        ended_at:   endedAt || null,
        notes:      notes || null,
      });
      onSaved();
      onClose();
    } catch (e) {
      console.error(e);
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Editar registro</h2>
          <button onClick={onClose} disabled={saving} className="text-muted-foreground hover:text-foreground">
            <X className="size-5" />
          </button>
        </div>

        <div className="mb-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
          <span className="font-medium">{ROLE_LABELS[position.role]}</span>
          {' — '}
          <span className="text-muted-foreground">{position.minister?.full_name}</span>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Fecha de inicio</label>
            <input
              type="date"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              disabled={saving}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {!isActive && (
            <div>
              <label className="mb-1 block text-sm font-medium">Fecha de fin</label>
              <input
                type="date"
                value={endedAt}
                onChange={(e) => setEndedAt(e.target.value)}
                disabled={saving}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}

          {isActive && (
            <p className="text-xs text-muted-foreground">
              Este cargo está activo. Para cerrarlo, usa el botón "Terminar cargo" en la lista.
            </p>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium">Notas <span className="font-normal text-muted-foreground">(opcional)</span></label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={saving}
              rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={saving} className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add historical record modal
// ---------------------------------------------------------------------------
interface AddHistoricalModalProps {
  orgType: 'DISTRICT' | 'REGION';
  orgId: string;
  districtId?: string;
  roles: AuthorityRole[];
  onClose: () => void;
  onSaved: () => void;
}

function AddHistoricalModal({ orgType, orgId, districtId, roles, onClose, onSaved }: AddHistoricalModalProps) {
  const [role, setRole] = useState<AuthorityRole>(roles[0]);
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [ministers, setMinisters] = useState<Minister[]>([]);
  const [selectedMinister, setSelectedMinister] = useState<Minister | null>(null);
  const [loadingMinisters, setLoadingMinisters] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!search.trim()) { setMinisters([]); return; }
    setLoadingMinisters(true);
    const t = setTimeout(async () => {
      try {
        const results = await searchMinistersForAuthority(
          search,
          orgType === 'DISTRICT' ? orgId : districtId,
          orgType === 'REGION' ? orgId : undefined,
          50
        );
        setMinisters(results);
      } finally {
        setLoadingMinisters(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search, orgType, orgId, districtId]);

  const handleSave = async () => {
    if (!selectedMinister) { setError('Selecciona un ministro.'); return; }
    if (!startedAt)        { setError('La fecha de inicio es obligatoria.'); return; }
    if (!endedAt)          { setError('La fecha de fin es obligatoria para registros históricos.'); return; }
    if (endedAt < startedAt) { setError('La fecha de fin no puede ser anterior a la de inicio.'); return; }

    setSaving(true);
    setError('');
    try {
      await addHistoricalAuthorityPosition({
        orgType,
        districtId: orgType === 'DISTRICT' ? orgId : undefined,
        regionId:   orgType === 'REGION'   ? orgId : undefined,
        ministerId: selectedMinister.id,
        role,
        startedAt,
        endedAt,
        notes: notes || undefined,
      });
      onSaved();
      onClose();
    } catch (e) {
      console.error(e);
      setError('Error al guardar. Es posible que ya exista un registro para ese cargo y período.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Agregar registro histórico</h2>
          <button onClick={onClose} disabled={saving} className="text-muted-foreground hover:text-foreground">
            <X className="size-5" />
          </button>
        </div>

        <p className="mb-4 text-xs text-muted-foreground">
          Solo para períodos pasados. Para asignar una autoridad activa, usa la pestaña de Información.
        </p>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Cargo</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as AuthorityRole)}
              disabled={saving}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {roles.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Fecha de inicio</label>
              <input type="date" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} disabled={saving}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Fecha de fin</label>
              <input type="date" value={endedAt} onChange={(e) => setEndedAt(e.target.value)} disabled={saving}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
          </div>

          {selectedMinister ? (
            <div className="flex items-center gap-3 rounded-md border border-border bg-muted/50 p-3">
              <User className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1 text-sm">
                <p className="font-medium">{selectedMinister.full_name}</p>
                <p className="text-xs text-muted-foreground">{selectedMinister.adb_code}</p>
              </div>
              <button onClick={() => setSelectedMinister(null)} className="text-muted-foreground hover:text-foreground">
                <X className="size-4" />
              </button>
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-sm font-medium">Buscar ministro</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Código o nombre..."
                disabled={saving}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {(loadingMinisters || ministers.length > 0) && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-border bg-card shadow-sm">
                  {loadingMinisters ? (
                    <div className="flex justify-center py-4"><Loader2 className="size-4 animate-spin" /></div>
                  ) : (
                    ministers.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => { setSelectedMinister(m); setSearch(''); setMinisters([]); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                      >
                        <User className="size-4 shrink-0 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{m.full_name}</p>
                          <p className="text-xs text-muted-foreground">{m.adb_code}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium">Notas <span className="font-normal text-muted-foreground">(opcional)</span></label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={saving} rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={saving} className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted">
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {saving ? <Loader2 className="size-4 animate-spin" /> : <PlusCircle className="size-4" />}
              Agregar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main HistorialTab
// ---------------------------------------------------------------------------
function formatDate(iso: string) {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export default function HistorialTab() {
  const { district, region, isDistrict } = useOutletContext<TerritoryContext>();
  const { user: authUser } = useAuth();
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [positions, setPositions] = useState<AuthorityPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState<AuthorityPosition | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [endingId, setEndingId] = useState<string | null>(null);

  const orgId = isDistrict ? district?.id : region?.id;
  const orgType = isDistrict ? 'DISTRICT' as const : 'REGION' as const;
  const roles = isDistrict ? DISTRICT_ROLES : REGION_ROLES;

  // Check admin status
  useEffect(() => {
    if (!authUser) return;
    getUserProfileByAuthId(authUser.id)
      .then((u) => setUserIsAdmin(isAdmin(u)))
      .catch(console.error);
  }, [authUser]);

  const loadPositions = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const data = await getAuthorityPositions(
        isDistrict ? { districtId: orgId } : { regionId: orgId }
      );
      setPositions(data);
    } finally {
      setLoading(false);
    }
  }, [orgId, isDistrict]);

  useEffect(() => { loadPositions(); }, [loadPositions]);

  const handleEnd = async (position: AuthorityPosition) => {
    if (!confirm(`¿Terminar el cargo de ${ROLE_LABELS[position.role]} para ${position.minister?.full_name}? Se establecerá hoy como fecha de fin.`)) return;
    setEndingId(position.id);
    try {
      await endAuthorityPosition(position);
      await loadPositions();
    } catch (e) {
      console.error(e);
      alert('Error al terminar el cargo. Intenta de nuevo.');
    } finally {
      setEndingId(null);
    }
  };

  // Group records by role, in the canonical order for this org type
  const byRole = roles.map((role) => ({
    role,
    records: positions.filter((p) => p.role === role),
  })).filter((g) => g.records.length > 0);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Historial de Autoridades</h2>
            <p className="text-sm text-muted-foreground">Registro completo de quién ocupó cada cargo y cuándo.</p>
          </div>
          {userIsAdmin && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <PlusCircle className="size-4" />
              Agregar histórico
            </button>
          )}
        </div>

        {byRole.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            No hay registros de historial. Los registros se crean al asignar autoridades.
          </div>
        ) : (
          byRole.map(({ role, records }) => (
            <div key={role} className="rounded-lg border border-border bg-card">
              <div className="border-b border-border px-5 py-3">
                <h3 className="text-sm font-semibold">{ROLE_LABELS[role]}</h3>
              </div>
              <div className="divide-y divide-border">
                {records.map((pos) => {
                  const isActive = !pos.ended_at;
                  const isEnding = endingId === pos.id;
                  return (
                    <div key={pos.id} className="flex items-center gap-4 px-5 py-3">
                      {/* Status indicator */}
                      <span className={`size-2.5 shrink-0 rounded-full ${isActive ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />

                      {/* Minister */}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{pos.minister?.full_name ?? '—'}</p>
                        <p className="text-xs text-muted-foreground">{pos.minister?.adb_code}</p>
                      </div>

                      {/* Date range */}
                      <div className="shrink-0 text-right text-sm">
                        <p>{formatDate(pos.started_at)} → {pos.ended_at ? formatDate(pos.ended_at) : <span className="font-medium text-green-600">Activo</span>}</p>
                        {pos.notes && <p className="text-xs text-muted-foreground">{pos.notes}</p>}
                      </div>

                      {/* Actions (admin only) */}
                      {userIsAdmin && (
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            onClick={() => setEditTarget(pos)}
                            title="Editar"
                            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            <Pencil className="size-4" />
                          </button>
                          {isActive && (
                            <button
                              onClick={() => handleEnd(pos)}
                              disabled={isEnding}
                              title="Terminar cargo"
                              className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                            >
                              {isEnding ? <Loader2 className="size-4 animate-spin" /> : <LogOut className="size-4" />}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {editTarget && (
        <EditModal
          position={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={loadPositions}
        />
      )}

      {showAddModal && orgId && (
        <AddHistoricalModal
          orgType={orgType}
          orgId={orgId}
          districtId={isDistrict ? orgId : region?.district_id}
          roles={roles}
          onClose={() => setShowAddModal(false)}
          onSaved={loadPositions}
        />
      )}
    </div>
  );
}
